import { linestringsToPolylines } from '@/mixins/map'
import historyAPI from '@/api/historyApi.js'
import api from '@/api'
import moment from 'moment'
import { formatSeconds, formatDatetimeWithSeconds } from '@/utils/dates.js'
import normalizePositionEntity from './entities/position-entity'
import createSensorConfigEntity from '@/services/entities/sensor-config.js'
import { getFormattedAddress } from '@/utils/address'
import { normalizeObject, getNestedValue } from '@/utils/object.js'
import normalizeTripHistoryStep from '@/services/entities/trip-history-step.js'
import { splitOperation } from '@/utils/promise.js'
import {
  formatTimeWithSeconds,
  getFormattedDurationFromSeconds,
} from '@/utils/dates.js'
import APIUrls, {
  APIV3RequestDatetimeFormat,
  APIV2RequestDatetimeFormat,
} from '@/config/simpliciti-apis.js'
import sensorsService from './sensors-service'
import * as R from 'ramda'
import hash from 'object-hash'

export default {
  getVehicleHistoryFromDate,
  normalizeVehicleHistoryPositions,
  normalizeTripHistoryItem,
}

/**
 * Used by Diagnostic module to fetch vehicle overview information about the trip history for a particular date.
 *
 * @param {Number} vehicleId
 * @param {Date|Moment} date
 * @returns {Object} Overview information about the trip history
 */
export async function getVehicleHistoryFromDate(vehicleId, date, options = {}) {
  return await historyAPI.getVehicleHistoryInfosFromAPIV2(
    vehicleId,
    date,
    options
  )
}

/**
 * Used by Diagnostic module
 *
 * @todo Use properties[positions][]= to reduce size of nested objects (It doesn't work with arrays like tor/ana)
 * @param {*} vehicleId
 * @param {*} date
 * @param {*} options
 * @version APIV3
 * @returns
 */
export async function getVehiclePositions(
  vehicleId,
  startDatetime,
  endDatetime
) {
  const startMoment = moment(startDatetime)
  const endMoment = moment(endDatetime)
  const durationHours = endMoment.diff(startMoment, 'hours', true)

  if (durationHours <= 4) {
    return fetchHandler(startDatetime, endDatetime)
  } else {
    let arr = await splitOperation({
      sequential: false,
      generateSubsets() {
        const subsets = []
        let currentStart = startMoment.clone()

        while (currentStart.isBefore(endMoment)) {
          const currentEnd = currentStart.clone().add(4, 'hours')
          const effectiveEnd = currentEnd.isBefore(endMoment)
            ? currentEnd
            : endMoment.clone()

          subsets.push({
            startDatetime: currentStart.clone(),
            endDatetime: effectiveEnd,
          })

          currentStart = effectiveEnd
        }

        // Process in chunks of 10 parallel requests
        const chunkSize = 10
        const chunkedSubsets = []
        for (let i = 0; i < subsets.length; i += chunkSize) {
          chunkedSubsets.push(subsets.slice(i, i + chunkSize))
        }
        return chunkedSubsets
      },
      async handleSubset(subset) {
        // Process each chunk of up to 10 requests in parallel
        const results = await Promise.all(
          subset.map((item) =>
            fetchHandler(item.startDatetime, item.endDatetime)
          )
        )
        return results.flat()
      },
    })

    // Flatten results from all chunks
    return arr.reduce((acc, val) => [...acc, ...val], [])
  }

  async function fetchHandler(startDatetime, endDatetime) {
    let data = []
    startDatetime = moment(startDatetime).format(APIV3RequestDatetimeFormat)
    endDatetime = moment(endDatetime).format(APIV3RequestDatetimeFormat)
    data = (
      await api.v3.get(APIUrls.APIV3_GET_POSITIONS, {
        vehicleIds: [vehicleId],
        startDatetime,
        endDatetime,
        groups: ['sensor_details', 'can', 'sweeper', 'washer'],
        properties: ['positions', 'vehicleRegistrationPlate', 'vehicleName'],
      })
    ).data
    let vehicleRegistrationPlate = data[0]?.vehicleRegistrationPlate
    data = data.length > 0 ? data[0] : { positions: [] }
    data = data.positions.map(function (item) {
      item.vehicleRegistrationPlate = vehicleRegistrationPlate
      return normalizePositionEntity(item, {
        vehicleId,
        datetimeInputFormat: 'YYYY-MM-DDTHH:mm:ss[Z]',
      })
    })

    const LOGIC_DIFF_MINUTES = 30

    //Filter out invalid positions
    data = data.filter((item, i) => {
      const hasValidLat = !!item.lat
      const hasValidLon = !!item.lng
      const gpsQualityNot0 = parseInt(item.gpsQuality) !== 0

      let hasLogicPositionBefore = false
      let hasLogicPositionAfter = false

      // Check for a position within -1 hour
      for (let j = i - 1; j >= 0; j--) {
        const timeDifference = moment(item.datetime).diff(
          data[j].datetime,
          'minutes'
        )

        // No need to check further if difference is more than 1 hour
        if (timeDifference > LOGIC_DIFF_MINUTES) break

        if (timeDifference <= LOGIC_DIFF_MINUTES) {
          hasLogicPositionBefore = true
          break
        }
      }

      // Check for a position within +1 hour
      for (let k = i + 1; k < data.length; k++) {
        const timeDifference = moment(data[k].datetime).diff(
          item.datetime,
          'minutes'
        )

        if (timeDifference > LOGIC_DIFF_MINUTES) break // No need to check further if difference is more than 1 hour

        if (timeDifference <= LOGIC_DIFF_MINUTES) {
          hasLogicPositionAfter = true
          break
        }
      }

      return (
        hasValidLat &&
        hasValidLon &&
        gpsQualityNot0 &&
        hasLogicPositionBefore &&
        hasLogicPositionAfter
      )
    })

    return data
  }
}

/**
 * Used by Diagnostic module
 * @todo Remove when unused
 *
 * @param {*} vehicleId
 * @param {*} datetime
 * @version APIV2
 * @returns
 */
export async function getVehicleHistoryPositionDetailsFromDateLegacy(
  vehicleId,
  datetime
) {
  datetime = datetime._isAMomentObject ? datetime : moment(new Date(datetime))
  datetime = datetime.format(APIV2RequestDatetimeFormat)
  let items = (
    await api.v2.get(
      `${APIUrls.APIV2_POSITIONS_GPS}?vehicule_id=${vehicleId}&dateheure_debut=${datetime}&dateheure_fin=${datetime}&groups=infos_complementaires,capteurs,capteurs_details,can,localisation_adresse`
    )
  ).data

  if (items instanceof Array && items.length > 0) {
    let item = items[0]
    let headers = R.omit(['positions'], item)
    item = {
      ...headers,
      ...(item.positions.length > 0 ? item.positions[0] : {}),
    }
    return normalizePositionEntity(item)
  } else {
    return null
  }
}

/**
 * Unused
 * @param {Array} positions
 * @param {String} vehicleId
 * @returns
 */
export function normalizeVehicleHistoryPositions(positions, vehicleId) {
  return (positions || []).map((item) => {
    return {
      lat: item.latitude,
      lng: item.longitude,
      datetime: item.datetime,
      timestamp: moment(item.datetime)._d.getTime(),
      vehicleId,
    }
  })
}

/***
 * Used by Diagnostic module
 * @version APIV3
 * Will compare two positions using APIV3_POSITIONS_CUMULATION
 */
export async function analyzeHistorySegment(
  vehicleId,
  datetimeFrom,
  datetimeTo
) {
  let res = await api.v3.get(APIUrls.APIV3_POSITIONS_CUMULATION, {
    vehicleId,
    startDatetime: moment(datetimeFrom).format(APIV3RequestDatetimeFormat),
    endDatetime: moment(datetimeTo).format(APIV3RequestDatetimeFormat),
  })
  return res.data instanceof Array && res.data.length > 0
    ? normalizeAnalyzeHistorySegment(res.data[0])
    : null
}

/**
 * Used by Diagnostic module
 * @todo Refactor/Move into entity "diagnostic-analysis-entity"
 * @param {*} response
 * @returns
 */
export function normalizeAnalyzeHistorySegment(response) {
  //response key --> normalized key
  let normalizedTable = {
    vehicleId: 'vehicleId',
    clientId: 'clientId',
    vehicleCategoryId: 'vehicleCategoryId',
    vehicleName: 'vehicleName',
    clientName: 'clientName',
    vehicleRegistrationPlate: 'vehicleRegistrationPlate',
    vehicleCategoryName: 'vehicleCategoryName',
    startDatetime: {
      key: 'startDatetime',
      transformKey: 'startDatetimeFormatted',
      transform: (v) =>
        formatDatetimeWithSeconds(v, {
          inputFormat: APIV3RequestDatetimeFormat,
        }),
    },
    endDatetime: {
      key: 'endDatetime',
      transformKey: 'endDatetimeFormatted',
      transform: (v) =>
        formatDatetimeWithSeconds(v, {
          inputFormat: APIV3RequestDatetimeFormat,
        }),
    },
    duration: {
      key: 'duration',
      transformKey: 'durationFormatted',
      transform: (v) => formatSeconds(v),
    },
    effectiveDuration: {
      key: 'effectiveDuration',
      transformKey: 'effectiveDurationFormatted',
      transform: (v) => formatSeconds(v),
    },
    distance: {
      key: 'distance',
      transformKey: 'distanceFormatted',
      transform: (v) => `${(v / 1000).toFixed(2)} Km`,
    },
    speed: {
      key: 'speed',
      transformKey: 'speedFormatted',
      transform: (v) => `${v} Km/h`,
    },
    totalCo2: 'totalCo2',
    averageCo2: 'averageCo2',
    totalConsumption: 'totalConsumption',
    averageConsumption: {
      key: 'averageConsumption',
      /*
      transformKey: 'averageConsumptionPerc',
      transform: (v, rawData) => {
        return (
          (rawData.averageConsumption / rawData.referenceConsumption) *
          100
        ).toFixed(0)
      },
      */
    },
    referenceConsumption: 'referenceConsumption',
    averageEngineSpeed: 'averageEngineSpeed',
    startAddress: {
      key: 'startAddress',
      transformKey: 'startAddressFormatted',
      transform: (v, rawData = {}) =>
        getFormattedAddress(rawData, {
          streetNumber: '',
          streetName: 'startAddress',
          zipCode: 'startZipCode',
          city: 'startCity',
        }),
    },
    startZipCode: 'startZipCode',
    startCity: 'startCity',
    startLongitude: 'startLongitude',
    startLatitude: 'startLatitude',
    endAddress: {
      key: 'endAddress',
      transformKey: 'endAddressFormatted',
      transform: (v, rawData = {}) =>
        getFormattedAddress(rawData, {
          streetNumber: '',
          streetName: 'endAddress',
          zipCode: 'endZipCode',
          city: 'endCity',
        }),
    },
    endZipCode: 'endZipCode',
    endCity: 'endCity',
    endLongitude: 'endLongitude',
    endLatitude: 'endLatitude',
  }
  return normalizeObject(response, normalizedTable)
}

/**
 *
 * Used by Location module (Trip history details - table) to load positions and sensors configuration
 *
 * @param {*} vehicleId
 * @param {*} date
 * @returns {Array}
 */
export async function getVehicleSensorsConfiguration(vehicleId) {
  /*   const t = (d, h = 0, m = 0, s = 0) =>
    moment(d).hour(h).minute(m).second(s).format(APIV2RequestDatetimeFormat) */

  const startDatetime = moment()
    .startOf('day')
    .format(APIV3RequestDatetimeFormat)
  const endDatetime = moment().endOf('day').format(APIV3RequestDatetimeFormat)

  let res = await sensorsService.getSensors(
    vehicleId,
    startDatetime,
    endDatetime,
    { sensors: true }
  )

  if (res) {
    return {
      id: res?.configurationId || -1,
      name: res?.configurationName || 'Default',
      sensors: (res?.sensors || []).map(createSensorConfigEntity),
    }
  } else {
    return {}
  }
}

/**
 * Used by Latest passed vehicles
 *
 * @param {Number} vehicleId
 * @param {Date} tripHistoryDate
 * @param {String} options.polylineColor
 */
export async function getTripHistoryListData(
  vehicleId,
  tripHistoryDate,
  options = {}
) {
  console.debugVerboseScope(
    ['location_module', 'last_passed_vehicles_feature'],
    5,
    'service::getTripHistoryListData(last_passed_vehicles_feature)',
    {
      vehicleId,
      tripHistoryDate,
      options,
    }
  )

  //Retrieve raw trip history data
  const rawTripHistoryData =
    (await historyAPI.getTripHistoryFromVehicleId(
      vehicleId,
      tripHistoryDate
    )) || []

  console.log({
    input: {
      vehicleId,
      tripHistoryDate,
      options,
    },
    rawTripHistoryData,
  })

  //Compute normalized trip history data
  let tripHistoryListData = null
  let rawTripHistoryFirst = rawTripHistoryData && rawTripHistoryData[0]
  if (rawTripHistoryFirst) {
    tripHistoryListData = rawTripHistoryFirst.troncons.map(
      normalizeTripHistoryItem(vehicleId)
    )
  }

  //Compute polylines
  const tripHistoryPolylinesData = linestringsToPolylines(tripHistoryListData, {
    ...(options.polylineColor
      ? {
          color: options.polylineColor,
        }
      : {}),
  })

  //Compute chart trip history data
  const tripHistoryListChartData =
    await historyAPI.getChartTripHistoryFromVehicleId(
      vehicleId,
      tripHistoryDate,
      rawTripHistoryData
    )

  return {
    tripHistoryListChartData,
    tripHistoryListData,
    tripHistoryPolylinesData,
  }
}

function getMilli(item, field) {
  return (
    (item[field] &&
      moment(item[field], APIV2RequestDatetimeFormat)._d.getTime()) ||
    0
  )
}

/**
 * @TODO: Use getNestedValue helper function to normalize attributes
 */
export function normalizeTripHistoryItem(vehicleId) {
  return function (item, index) {
    let mappedItem = {
      number: parseInt(index + 1),
      vehicleId: vehicleId,
      distance: item.distance,
      tripDuration: item.duree_trajet,
      tripDurationFormatted: getFormattedDurationFromSeconds(item.duree_trajet),
      date: moment(item.date, 'YYYY-MM-DD')._d,
      stopDurationSeconds: item.duree_arret,
      stopDuration: getFormattedDurationFromSeconds(item.duree_arret),
      fromTimeMilli: getMilli(item, 'dh_trajet_deb'),
      fromTime: getTime(item, 'dh_trajet_deb'),
      fromAddress: getAddress(
        item,
        '',
        'adresse_deb',
        'cp_deb',
        'ville_deb',
        true,
        'zone_deb'
      ),
      toTime: getTime(item, 'dh_trajet_fin'),
      toTimeMilli: getMilli(item, 'dh_trajet_fin'),
      toAddress: getAddress(
        item,
        '',
        'adresse_fin',
        'cp_fin',
        'ville_fin',
        true,
        'zone_fin'
      ),
      datetimeContactON: getTime(item, 'dh_contact_on'),
      datetimeContactONMilli: getMilli(item, 'dh_contact_on'),
      datetimeContactOFF: getTime(item, 'dh_contact_off'),
      datetimeContactOFFMilli: getMilli(item, 'dh_contact_off'),
      linestring: item.linestring,
      speedLinestring: getNestedValue(
        item,
        ['linestring_mouvement', 'linestring_couleur'],
        []
      ),
    }
    mappedItem.id = hash(mappedItem)
    return mappedItem
  }
}

function getTime(item, field) {
  return formatTimeWithSeconds(item[field], {
    inputFormat: APIV2RequestDatetimeFormat,
  })
}

function getAddress(
  item,
  streetNumber,
  streetAddress,
  zipCode,
  city,
  useZone,
  zone
) {
  return getFormattedAddress(item, {
    streetNumber,
    streetAddress,
    zipCode,
    city,
    useZone: useZone === true ? true : item[useZone],
    zone,
  })
}

/**
 * Fill gaps in polylines array to resolve Leaflet polyline UX issue.
 * Adds first lat/lng from the next element (if exists) to fill gaps.
 *
 * @param {Array} polylines - Array of polylines with lat/lng data.
 * @returns {Array} Updated array of polylines with filled gaps.
 */
export function fillPolylinesGaps(polylines) {
  const filledPolylines = polylines.map((polyline, index, array) => {
    if (index < array.length - 1) {
      const currentPolyline = polyline.polyline
      const nextPolyline = array[index + 1].polyline

      if (currentPolyline.length > 0 && nextPolyline.length > 0) {
        // Add the first lat/lng from the next element to fill gaps
        const lastLatLng = currentPolyline[currentPolyline.length - 1]
        const firstNextLatLng = nextPolyline[0]

        // Check if there's a gap by comparing the last point of the current polyline
        // with the first point of the next polyline
        if (
          lastLatLng[0] !== firstNextLatLng[0] ||
          lastLatLng[1] !== firstNextLatLng[1]
        ) {
          // If there's a gap, add the first point of the next polyline to fill it
          currentPolyline.push(firstNextLatLng)
        }
      }
    }
    return polyline
  })

  return filledPolylines
}
